import {useMemo} from 'react';

import {assert} from 'Asserts';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {DEFAULT_MEASUREMENT} from 'constants/MeasurementConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrame_dataFrame_InaccessibleInfoNode as InaccessibleInfoGraphQL,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {
  GET_PERSISTED_DATA_FRAME_QUERY,
  PersistedDataFrameGraphQL,
} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetScratchPlateDesignSummary,
  GetScratchPlateDesignSummaryVariables,
  GetScratchPlateDesignSummary_scratchPlate_ScratchPlateNode as ScratchPlateNode,
} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlateDesignSummary';
import {wellPositionKey} from 'notebook/integrations/plate-maps/scratch-plates/FilledScratchPlateUtil';
import {GET_SCRATCH_PLATE_DESIGN_SUMMARY_QUERY} from 'notebook/integrations/plate-maps/scratch-plates/GetScratchPlateDesignSummary';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {
  MetadataLayerInfo,
  PlateMap,
  PlateMapWell,
  SchematizedDisplayLayerMetadata,
} from 'samples/plate-maps/PlateMapConstants';
import {
  deserializeSchemaTags,
  SerializedSchemaTag,
} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import {DEFAULT_MEASUREMENT_VALIDATION_ERRORS} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {UnitsQuery, UnitsQueryVariables} from 'units/__generated/UnitsQuery';
import {UNITS_QUERY} from 'units/UnitsConstants';

export const LABEL_LAYER_NAME = 'label';
export const CONCENTRATION_DESIGN_LAYER_NAME = 'concentration_design';
export const METADATA_LAYER_NAME = 'metadata';

function isPersistedDataFrame(
  item: {__typename?: string} | null | undefined
): item is PersistedDataFrameGraphQL {
  return item?.__typename === 'DataFrameNode';
}

function useDataFrame(dataFrameId: string | null): {
  dataFrame: PersistedDataFrameGraphQL | InaccessibleInfoGraphQL | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const result = useSkippableBenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>(
    GET_PERSISTED_DATA_FRAME_QUERY,
    {
      variables: {dataFrameId: dataFrameId ?? '', limit: DEFAULT_DATA_FRAME_ROW_LIMIT},
      skip: dataFrameId == null,
    }
  );

  if (dataFrameId == null) {
    return {dataFrame: undefined, isLoading: false, isError: false};
  }
  if (result.type === QueryResultType.PENDING) {
    return {dataFrame: undefined, isLoading: true, isError: false};
  }
  if (result.type === QueryResultType.ERROR) {
    return {dataFrame: undefined, isLoading: false, isError: true};
  }
  if (result.type !== QueryResultType.SUCCESS) {
    return {dataFrame: undefined, isLoading: false, isError: false};
  }
  if (isInaccessible(result.data.dataFrame)) {
    return {dataFrame: result.data.dataFrame, isLoading: false, isError: false};
  }
  if (isPersistedDataFrame(result.data.dataFrame)) {
    return {dataFrame: result.data.dataFrame, isLoading: false, isError: false};
  }
  return {dataFrame: undefined, isLoading: false, isError: false};
}
interface ConcentrationLayerRow {
  layerName: string;
  rowIndex: number;
  columnIndex: number;
  amount: number | null;
  unitApiId: string;
  unitVersionNumber: number;
}

interface ParsedConcentrationDesign {
  rows: Array<ConcentrationLayerRow>;
  unitApiIds: Array<string>;
}

/** Extract row data and unit IDs from the `concentration_design` data frame. */
function parseConcentrationDesignDataFrame(dataFrame: DataFrame): ParsedConcentrationDesign {
  const rows: Array<ConcentrationLayerRow> = [];
  const unitApiIdsSet: Set<string> = new Set();

  for (const dataFrameRow of dataFrame.rows) {
    if (dataFrameRow._concentration_unit_api_id == null) {
      continue;
    }

    const unitApiId = dataFrameRow._concentration_unit_api_id as string;
    rows.push({
      layerName: dataFrameRow._concentration_layer_name as string,
      rowIndex: dataFrameRow._row_index as number,
      columnIndex: dataFrameRow._column_index as number,
      amount:
        dataFrameRow._concentration_amount != null
          ? parseFloat(dataFrameRow._concentration_amount as string)
          : null,
      unitApiId,
      unitVersionNumber: dataFrameRow._concentration_unit_version_number as number,
    });
    unitApiIdsSet.add(unitApiId);
  }
  return {
    rows,
    unitApiIds: Array.from(unitApiIdsSet),
  };
}

interface ConcentrationDesignInformation {
  rows: Array<ConcentrationLayerRow>;
  // Hydrated mapping of unit ID to unit information
  unitsByApiId: Map<string, MeasurementUnit>;
}

/** Parse the `concentration_design` data frame, then hydrate any referenced units. */
function useConcentrationDesignInformation(
  concentrationDataFrame: PersistedDataFrameGraphQL | undefined
): ConcentrationDesignInformation & {isLoading: boolean} {
  const {rows, unitApiIds} = useMemo((): ParsedConcentrationDesign => {
    if (concentrationDataFrame?.dataFrameJson == null) {
      return {
        rows: [],
        unitApiIds: [],
      };
    }
    return parseConcentrationDesignDataFrame(concentrationDataFrame.dataFrameJson);
  }, [concentrationDataFrame]);
  const unitsResult = useSkippableBenchlingQuery<UnitsQuery, UnitsQueryVariables>(UNITS_QUERY, {
    variables: {ids: unitApiIds},
    skip: unitApiIds.length === 0,
  });
  const unitsByApiId = useMemo((): Map<string, MeasurementUnit> => {
    if (unitsResult.type !== QueryResultType.SUCCESS) {
      return new Map();
    }
    return new Map(unitsResult.data.units.map((u) => [u.id, u as unknown as MeasurementUnit]));
  }, [unitsResult]);

  return {
    rows,
    unitsByApiId,
    isLoading: unitApiIds.length > 0 && unitsResult.type === QueryResultType.PENDING,
  };
}

// Keep aligned with METADATA_ALLOWED_LEAF_TYPES in MetadataLayerSettings so authored layer types
// round-trip on edit. DATETIME is intentionally excluded for now (see that file).
const METADATA_ALLOWED_LEAF_TYPES_SET = new Set<LeafType>([
  LeafType.TEXT,
  LeafType.INTEGER,
  LeafType.DECIMAL,
  LeafType.BOOLEAN,
  LeafType.DATE,
]);

export function leafTypeToMetadataType(leafType: LeafType): LeafType {
  return METADATA_ALLOWED_LEAF_TYPES_SET.has(leafType) ? leafType : LeafType.TEXT;
}

export function parseMetadataLayerInfo(allColumnsJson: DataFrame['columns']): Array<MetadataLayerInfo> {
  return allColumnsJson
    .filter((col) => !col.systemName.startsWith('_'))
    .map((col) => ({
      layerId: col.systemName,
      name: col.displayName,
      type: leafTypeToMetadataType(col.type.leafType),
    }));
}

function coerceMetadataValue(raw: unknown, leafType: LeafType): string | number | boolean | null {
  if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
    return null;
  }
  if (leafType === LeafType.INTEGER) {
    const parsed = typeof raw === 'number' ? raw : Number(String(raw));
    return Number.isInteger(parsed) ? parsed : null;
  }
  if (leafType === LeafType.DECIMAL) {
    const parsed = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (leafType === LeafType.BOOLEAN) {
    if (typeof raw === 'boolean') {
      return raw;
    }
    const normalized = String(raw).trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
    return null;
  }

  // TEXT / DATE / DATETIME are consumed as string render values in display layers.
  return String(raw);
}

export function parseMetadataValues(
  dataFrame: DataFrame,
  layers: Array<MetadataLayerInfo>
): Map<string, Record<string, string | number | boolean | null>> {
  const result = new Map<string, Record<string, string | number | boolean | null>>();
  for (const row of dataFrame.rows) {
    const posKey = wellPositionKey(row._row_index as number, row._column_index as number);
    const values: Record<string, string | number | boolean | null> = {};
    for (const layer of layers) {
      values[layer.layerId] = coerceMetadataValue(row[layer.layerId], layer.type);
    }
    result.set(posKey, values);
  }
  return result;
}

/** Build the PlateMap object that is used by front-end components. */
export function buildPlateMap(
  plate: ScratchPlateNode,
  labelDataFrame: DataFrame,
  concentrationRows: Array<ConcentrationLayerRow>,
  unitsByApiId: Map<string, MeasurementUnit>,
  metadataLayers?: Array<MetadataLayerInfo>,
  metadataValuesByWell?: Map<string, Record<string, string | number | boolean | null>>
): PlateMap {
  // Build a map from layerName -> layerId (generated from position in unique layer names)
  const layerNames = [...new Set(concentrationRows.map((r) => r.layerName))];
  // Use layer name as the layerId key (consistent with how the modal names layers)
  const layerIdByName = new Map(layerNames.map((name) => [name, `persisted-layer-${name}`]));

  // Build per-well concentration map: (row,col) -> {layerId -> Measurement}
  const concentrationByWellPos = new Map<
    string,
    Map<string, {value: number | null; unit: MeasurementUnit | null}>
  >();
  for (const row of concentrationRows) {
    const posKey = wellPositionKey(row.rowIndex, row.columnIndex);
    if (!concentrationByWellPos.has(posKey)) {
      concentrationByWellPos.set(posKey, new Map());
    }
    const layerId = layerIdByName.get(row.layerName)!;
    const unit = unitsByApiId.get(row.unitApiId) ?? null;
    concentrationByWellPos.get(posKey)!.set(layerId, {value: row.amount, unit});
  }

  // Group rows by (row, column) — the label data frame may emit multiple rows per well (one per role).
  // We take the first row's role as the well's role and deduplicate to one well per position.
  const rowsByPosition = new Map<string, (typeof labelDataFrame.rows)[number]>();
  for (const row of labelDataFrame.rows) {
    const posKey = wellPositionKey(row._row_index as number, row._column_index as number);
    if (!rowsByPosition.has(posKey)) {
      rowsByPosition.set(posKey, row);
    }
  }

  const wells: Array<PlateMapWell> = Array.from(rowsByPosition.values()).map((row) => {
    const rowIndex = row._row_index as number;
    const columnIndex = row._column_index as number;
    const roleStr = row._role as string | null;
    const subrole = (row._subrole as string | null) ?? null;
    const groupRaw = row._group;
    assert(
      groupRaw == null || !Number.isNaN(Number(groupRaw)),
      `Error parsing scratch plate ${plate.id}: Expected _group to be a number, got ${groupRaw} (${typeof groupRaw})`
    );
    const group = groupRaw != null ? Number(groupRaw) : 0;

    let role: ExperimentalRole | null = null;
    if (
      roleStr != null &&
      Object.values(PrimaryExperimentalRoleType).includes(roleStr as PrimaryExperimentalRoleType)
    ) {
      role = {
        primaryRole: roleStr as PrimaryExperimentalRoleType,
        subrole,
        group,
      };
    }

    const wellConcentrations =
      concentrationByWellPos.get(wellPositionKey(rowIndex, columnIndex)) ?? new Map();
    const concentrationLayers: PlateMapWell['concentrationLayers'] = {};
    for (const [layerId, measurement] of wellConcentrations) {
      if (measurement.value != null && measurement.unit != null) {
        concentrationLayers[layerId] = {value: measurement.value, unit: measurement.unit};
      }
    }

    const schemaTags = deserializeSchemaTags(row._schema_tags as Record<string, SerializedSchemaTag> | null);

    const wellMetadata = metadataValuesByWell?.get(wellPositionKey(rowIndex, columnIndex)) ?? {};

    return {
      position: {row: rowIndex, column: columnIndex},
      role,
      sources: [] as PlateMapWell['sources'],
      contents: [] as PlateMapWell['contents'],
      schemaTags,
      dataParameterTags: {} as PlateMapWell['dataParameterTags'],
      quantity: DEFAULT_MEASUREMENT,
      finalQuantity: {measurement: DEFAULT_MEASUREMENT, errors: DEFAULT_MEASUREMENT_VALIDATION_ERRORS},
      concentrationLayers,
      metadataLayers: Object.keys(wellMetadata).length > 0 ? wellMetadata : undefined,
    };
  });

  const concentrationLayerInfoById =
    layerNames.length > 0
      ? Object.fromEntries(
          layerNames.map((name) => {
            const layerId = layerIdByName.get(name)!;
            return [layerId, {layerId, name}];
          })
        )
      : undefined;

  const metadataLayerInfoById: {[layerId: string]: MetadataLayerInfo} | undefined =
    metadataLayers && metadataLayers.length > 0
      ? Object.fromEntries(metadataLayers.map((l) => [l.layerId, l]))
      : undefined;

  return {
    id: plate.id,
    size: {numRows: plate.height, numColumns: plate.width},
    wells,
    concentrationLayerInfoById,
    metadataLayerInfoById,
  };
}

interface ScratchPlateData {
  // Underlying GraphQL query state; callers branch on `result.type` and
  // `isInaccessible(result.data.scratchPlate)` rather than a hand-rolled status enum.
  scratchPlateQuery: ReturnType<
    typeof useSkippableBenchlingQuery<GetScratchPlateDesignSummary, GetScratchPlateDesignSummaryVariables>
  >;
  // Result of GET_SCRATCH_PLATE_DESIGN_SUMMARY_QUERY
  plate: ScratchPlateNode | null;
  // Object that is recognized by our client-side plate components; essentially a translation of `plate`
  plateMap: PlateMap | null;
  // If `plate` has a linked plate schema, then this is the plate's owning container schema's ID
  containerSchemaId: string | null;
  // True if the current user lacks permission to view one or more of the plate's backing data frames
  dataFrameIsInaccessible: boolean;
  // True while the plate's backing data frame queries are still loading.
  dataFrameQueriesPending: boolean;
  // True if one of the plate's backing data frame queries failed.
  dataFrameQueriesError: boolean;
  // Callback to fetch `plate` again
  refetch: () => void;
}

/**
 * Fetch the ScratchPlate object and its `label`, `concentration_design`, and optional
 * `metadata` data frame layers to assemble a full PlateMap.
 */
export function useScratchPlateData(scratchPlateId: string): ScratchPlateData {
  const scratchPlateQuery = useSkippableBenchlingQuery<
    GetScratchPlateDesignSummary,
    GetScratchPlateDesignSummaryVariables
  >(GET_SCRATCH_PLATE_DESIGN_SUMMARY_QUERY, {variables: {id: scratchPlateId}});

  // plate + labelDataFrame + concentrationDataFrame has all information necessary to render the plate design
  const plate: ScratchPlateNode | null =
    scratchPlateQuery.type === QueryResultType.SUCCESS &&
    scratchPlateQuery.data.scratchPlate.__typename === 'ScratchPlateNode'
      ? scratchPlateQuery.data.scratchPlate
      : null;
  const labelDataFrameId = plate?.layers.find((l) => l.name === LABEL_LAYER_NAME)?.dataFrameId ?? null;
  const {
    dataFrame: labelDataFrame,
    isLoading: labelDataFrameIsLoading,
    isError: labelDataFrameIsError,
  } = useDataFrame(labelDataFrameId);
  const concentrationDataFrameId =
    plate?.layers.find((l) => l.name === CONCENTRATION_DESIGN_LAYER_NAME)?.dataFrameId ?? null;
  const {
    dataFrame: concentrationDataFrame,
    isLoading: concentrationDataFrameIsLoading,
    isError: concentrationDataFrameIsError,
  } = useDataFrame(concentrationDataFrameId);
  const metadataDataFrameId = plate?.layers.find((l) => l.name === METADATA_LAYER_NAME)?.dataFrameId ?? null;
  const {
    dataFrame: metadataDataFrame,
    isLoading: metadataDataFrameIsLoading,
    isError: metadataDataFrameIsError,
  } = useDataFrame(metadataDataFrameId);

  // Translate concentration data frame into row data and hydrate its unit references
  const {
    rows: concentrationRows,
    unitsByApiId,
    isLoading: unitsIsLoading,
  } = useConcentrationDesignInformation(
    isInaccessible(concentrationDataFrame) ? undefined : concentrationDataFrame
  );

  const dataFrameIsInaccessible =
    isInaccessible(labelDataFrame) ||
    isInaccessible(concentrationDataFrame) ||
    isInaccessible(metadataDataFrame);
  const dataFrameQueriesPending =
    labelDataFrameIsLoading ||
    concentrationDataFrameIsLoading ||
    metadataDataFrameIsLoading ||
    unitsIsLoading;
  const dataFrameQueriesError =
    labelDataFrameIsError || concentrationDataFrameIsError || metadataDataFrameIsError;

  const accessibleLabelDataFrame = isInaccessible(labelDataFrame) ? undefined : labelDataFrame;
  const accessibleMetadataDataFrame = isInaccessible(metadataDataFrame) ? undefined : metadataDataFrame;

  const {parsedMetadataLayers, metadataValuesByWell} = useMemo(() => {
    if (
      accessibleMetadataDataFrame?.dataFrameJson == null ||
      accessibleMetadataDataFrame.allColumnsJson == null
    ) {
      return {parsedMetadataLayers: undefined, metadataValuesByWell: undefined};
    }
    const cols = accessibleMetadataDataFrame.allColumnsJson as DataFrame['columns'];
    const layers = parseMetadataLayerInfo(cols);
    const values = parseMetadataValues(accessibleMetadataDataFrame.dataFrameJson, layers);
    return {parsedMetadataLayers: layers, metadataValuesByWell: values};
  }, [accessibleMetadataDataFrame]);

  const plateMap = useMemo(() => {
    if (plate == null) {
      return null;
    }
    if (accessibleLabelDataFrame == null || accessibleLabelDataFrame.dataFrameJson == null) {
      return null;
    }
    return buildPlateMap(
      plate,
      accessibleLabelDataFrame.dataFrameJson,
      concentrationRows,
      unitsByApiId,
      parsedMetadataLayers,
      metadataValuesByWell
    );
  }, [
    plate,
    accessibleLabelDataFrame,
    concentrationRows,
    unitsByApiId,
    parsedMetadataLayers,
    metadataValuesByWell,
  ]);

  const containerSchemaId = useMemo(() => {
    if (plate?.plateSchemaId == null) {
      return null;
    }
    return SampleSchemaStore.getPlateSchema(plate.plateSchemaId)?.containerSchemaId ?? null;
  }, [plate]);

  return {
    scratchPlateQuery,
    plate,
    plateMap,
    containerSchemaId,
    dataFrameIsInaccessible,
    dataFrameQueriesPending,
    dataFrameQueriesError,
    refetch: scratchPlateQuery.refetch,
  };
}

/**
 * Filter `wellSchemaLayers` down to the fields that actually have a
 * non-null value on at least one well.
 */
export function useWellSchemaOptionsForPlateMap(
  plateMap: PlateMap | null,
  wellSchemaLayers: Array<SchematizedDisplayLayerMetadata> | null
): Array<SchematizedDisplayLayerMetadata> {
  return useMemo((): Array<SchematizedDisplayLayerMetadata> => {
    if (wellSchemaLayers == null || plateMap == null) {
      return [];
    }
    const populatedFieldIds = new Set(
      plateMap.wells.flatMap((well) =>
        Object.entries(well.schemaTags)
          .filter(([, tag]) => {
            const v =
              tag.floatValue ??
              tag.integerValue ??
              tag.booleanValue ??
              tag.value ??
              tag.dateValue ??
              tag.datetimeValue ??
              tag.schemaFieldSelectorOptionIds;
            return v != null;
          })
          .map(([apiId]) => apiId)
      )
    );
    return wellSchemaLayers.filter((layer) => populatedFieldIds.has(layer.attribute.key));
  }, [wellSchemaLayers, plateMap]);
}
